import { z } from 'zod';
import { CanvasTextListPrefixSchema } from './textListPrefix.js';
import { CANVAS_FORMAT_TEXTS_MAX, CanvasFormatPlaceSchema, CanvasParagraphTargetSchema } from './canvasTarget.js';

/**
 * La forme d'un acte telle que la lit `canvas_inspect` avec `formatOf` (#1859,
 * spec #1858) : pour chaque paragraphe, son style nommé, sa forme de
 * paragraphe et la forme de chaque passage de texte.
 *
 * Unités d'avocat, les seules que l'agent voit : points pour la taille et
 * l'espacement, centimètres pour les retraits, multiple de l'interligne simple
 * pour l'interligne. Les conversions avec les unités du fichier Word vivent
 * dans le navigateur (`unitesDeForme.ts`).
 */

/**
 * Nombre maximal de paragraphes rendus par UNE lecture de forme ; le reste se
 * demande par `format.continuation`.
 *
 * WHY 250 : c'est une borne de SORTIE, pour le contexte du modèle — la même
 * échelle que la page maximale d'une lecture ciblée et que le plafond de
 * repères d'une lecture globale (`CANVAS_INSPECT_BLOCK_LIMIT_MAX`,
 * `CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX`). L'agent tient donc d'un seul appel
 * la forme de tout ce qu'une lecture a pu lui rendre ciblable. La valeur est
 * recopiée et non importée : `actionCatalog.ts` importe ce module, l'inverse
 * ferait un cycle ; `CanvasToolDefinitions.test.ts` garde l'égalité.
 */
export const CANVAS_FORMAT_READ_PARAGRAPH_MAX = 250;

/**
 * Longueur maximale du texte rendu pour un passage. La lecture de forme sert à
 * situer un passage et à lire sa forme, pas à relire l'acte : le texte complet
 * vient de la lecture du document.
 *
 * WHY 120 : valeur de jugement, non mesurée — une à deux lignes d'acte, assez
 * pour reconnaître le passage sans ambiguïté. Avec la borne de paragraphes,
 * elle tient le texte d'une lecture pleine autour de 30 000 caractères quand
 * chaque paragraphe est d'une seule forme, un dixième du budget d'une lecture
 * de document (`CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH`).
 */
export const CANVAS_FORMAT_READ_RUN_TEXT_MAX = 120;

/** Réglages de texte lus par passage. L'ordre est celui des tables du lecteur et de la description de l'outil. */
export const CANVAS_RUN_FORMAT_KEYS = ['bold', 'italic', 'underline', 'highlight', 'color', 'fontFamily', 'fontSizePt'] as const;
export type CanvasRunFormatKey = (typeof CANVAS_RUN_FORMAT_KEYS)[number];

/** Réglages lus par paragraphe. */
export const CANVAS_PARAGRAPH_FORMAT_KEYS = [
  'alignment',
  'indentLeftCm',
  'indentRightCm',
  'indentFirstLineCm',
  'spaceBeforePt',
  'spaceAfterPt',
  'lineSpacing',
  'border',
  'borderWidthPt',
] as const;
export type CanvasParagraphFormatKey = (typeof CANVAS_PARAGRAPH_FORMAT_KEYS)[number];

/**
 * Les deux fonds que `format_paragraphs` écrit : celui du paragraphe
 * (`shading`, `w:pPr/w:shd`) et celui de la cellule de tableau qui le contient
 * (`cellShading`, `w:tcPr/w:shd`). Ils sont à part de
 * {@link CANVAS_PARAGRAPH_FORMAT_KEYS} parce que la lecture de forme par
 * origine (`formatOf`) ne les publie pas : l'agent les lit dans la lecture en
 * lignes de l'acte (« fond 554E94 »), et le reçu les juge sur le fichier relu.
 */
export const CANVAS_SHADING_SETTING_KEYS = ['shading', 'cellShading'] as const;
export type CanvasShadingSettingKey = (typeof CANVAS_SHADING_SETTING_KEYS)[number];

/**
 * Le cadre de la case de tableau qui contient un paragraphe
 * (`w:tcPr/w:tcBorders`), que `format_paragraphs` écrit sous ce nom. Sa valeur
 * est celle de `border` ({@link cotesDeLaBordure}) et dit tout l'état de la
 * case : les côtés nommés sont tracés, les autres retirés. Comme les fonds, il
 * se lit dans la lecture en lignes de l'acte et se juge sur le fichier relu.
 */
export const CANVAS_CELL_BORDER_SETTING_KEY = 'cellBorder';

/**
 * Réglages que `format_paragraphs` ÉCRIT, dans l'ordre du reçu : le style nommé
 * (`styleId`, l'identifiant d'un style de paragraphe que le fichier définit),
 * les réglages lus par paragraphe, les fonds, puis le cadre de la case. Le style est à part de
 * {@link CANVAS_PARAGRAPH_FORMAT_KEYS} parce que la lecture ne le rend pas par
 * origine : il EST l'origine `style` des autres.
 */
export const CANVAS_PARAGRAPH_SETTING_KEYS = ['styleId', ...CANVAS_PARAGRAPH_FORMAT_KEYS, ...CANVAS_SHADING_SETTING_KEYS, CANVAS_CELL_BORDER_SETTING_KEY] as const;
export type CanvasParagraphSettingKey = (typeof CANVAS_PARAGRAPH_SETTING_KEYS)[number];

export const CanvasRunFormatSchema = z
  .object({
    bold: z.boolean(),
    italic: z.boolean(),
    underline: z.boolean(),
    /**
     * Surlignage, sous le nom de couleur que porte le fichier Word (`yellow`,
     * `green`, `magenta`…). `none` : le passage n'est pas surligné — c'est aussi
     * la valeur qu'écrit Word pour retirer un surlignage venu d'un style.
     */
    highlight: z.string().min(1),
    /**
     * Couleur du texte : `#RRGGBB` en capitales, ou `auto` — la couleur
     * automatique de Word (noir sur fond clair), celle d'un texte sans couleur.
     */
    color: z.string().min(1),
    fontFamily: z.string().min(1),
    fontSizePt: z.number().positive(),
  })
  .strict();
export type CanvasRunFormat = z.infer<typeof CanvasRunFormatSchema>;

export const CanvasParagraphAlignmentSchema = z.enum(['left', 'center', 'right', 'justify']);
export type CanvasParagraphAlignment = z.infer<typeof CanvasParagraphAlignmentSchema>;

/** Les quatre côtés d'une bordure de paragraphe, dans l'ordre où la lecture les nomme. */
export const CANVAS_PARAGRAPH_BORDER_SIDES = ['top', 'left', 'bottom', 'right'] as const;
export type CanvasParagraphBorderSide = (typeof CANVAS_PARAGRAPH_BORDER_SIDES)[number];

/**
 * Les côtés que trace une bordure, telle que la lecture la rend et que
 * `format_paragraphs` la pose : aucun (`none`), les quatre (`box`), ou les
 * côtés nommés, joints par `+` dans l'ordre de
 * {@link CANVAS_PARAGRAPH_BORDER_SIDES} (`bottom`, `top+bottom`).
 *
 * @returns Les côtés tracés ; `undefined` quand la valeur n'est pas de cette
 *   forme — côté inconnu ou répété, autre ordre, quatre côtés écrits un à un
 *   au lieu de `box`. La lecture ne rend que cette forme
 *   (`resumerLaBordure`, `@hector/docx-forme`) : c'est ce qui permet de juger
 *   une bordure posée par simple égalité avec la bordure relue.
 */
export function cotesDeLaBordure(border: string): CanvasParagraphBorderSide[] | undefined {
  if (border === 'none') return [];
  if (border === 'box') return [...CANVAS_PARAGRAPH_BORDER_SIDES];
  const nommes = border.split('+');
  const dansLOrdre = CANVAS_PARAGRAPH_BORDER_SIDES.filter((cote) => nommes.includes(cote));
  // Au moins un côté (une chaîne vide n'en nomme aucun), au plus trois : les quatre s'écrivent `box`.
  const nombreAdmis = dansLOrdre.length > 0 && dansLOrdre.length < CANVAS_PARAGRAPH_BORDER_SIDES.length;
  return nombreAdmis && dansLOrdre.join('+') === border ? dansLOrdre : undefined;
}

/**
 * La bordure qu'on POSE sur un paragraphe (`format_paragraphs`, #1867, côtés
 * choisis depuis le 2026-10-11) : ce que la lecture rend ({@link cotesDeLaBordure}).
 * La valeur dit tout l'état : les côtés nommés sont tracés, les autres retirés.
 * `left` seul est le trait de marge d'Hector.
 *
 * WHY un contrôle hors du schéma livré au modèle (`refine`) plutôt qu'une
 * énumération des seize valeurs : le schéma des verbes de forme part au modèle
 * à chaque tour sous un plafond de poids (`CanvasToolDefinitions.test.ts`), et
 * la description du champ dit la forme en moins de mots.
 */
const bordureParCotes = (champ: 'border' | typeof CANVAS_CELL_BORDER_SETTING_KEY) => z.string().refine(
  (border) => cotesDeLaBordure(border) !== undefined,
  (border) => ({
    message: `${champ} must be "none", "box", or the drawn sides joined by "+" in the order top, left, bottom, right — "bottom", "top+bottom", "left+bottom+right" (got ${JSON.stringify(border)})`,
  }),
);
export const CanvasParagraphBorderSchema = bordureParCotes('border');
export type CanvasParagraphBorder = z.infer<typeof CanvasParagraphBorderSchema>;

/** Le cadre qu'on POSE sur une case de tableau (`format_paragraphs`, {@link CANVAS_CELL_BORDER_SETTING_KEY}) : la forme de {@link CanvasParagraphBorderSchema}, refusée sous son propre nom. */
export const CanvasCellBorderSchema = bordureParCotes(CANVAS_CELL_BORDER_SETTING_KEY);

/** Le mot qui retire un fond : celui que la lecture de forme emploie pour « pas de fond ». */
export const CANVAS_SHADING_NONE = 'none';

/**
 * Un fond tel que la lecture de forme le rend et que le reçu le nomme : six
 * chiffres hexadécimaux en capitales, sans `#` (« 554E94 »), ou
 * {@link CANVAS_SHADING_NONE}.
 *
 * @returns Le fond sous cette forme ; `undefined` quand la valeur n'est ni une
 *   couleur hexadécimale (avec ou sans `#`, casse libre) ni `none`.
 *
 * WHY normaliser ici : l'éditeur écrit la valeur d'un fond de paragraphe TELLE
 * QUELLE dans `w:fill` — `#554E94`, `d9d9d9` et `yellow` compris, qui ne sont
 * pas des valeurs du format (`mesure-traits-et-fonds`, F3, 2026-10-11).
 */
export function fondNormalise(valeur: string): string | undefined {
  if (valeur === CANVAS_SHADING_NONE) return CANVAS_SHADING_NONE;
  return /^#?[0-9A-Fa-f]{6}$/.test(valeur) ? valeur.replace(/^#/, '').toUpperCase() : undefined;
}

/** Le fond qu'on POSE sur un paragraphe ou sur une cellule de tableau (`format_paragraphs`) : voir {@link fondNormalise}. */
export const CanvasShadingSchema = z.string().refine(
  (valeur) => fondNormalise(valeur) !== undefined,
  (valeur) => ({ message: `a shading must be a six-digit hex colour such as "554E94", or "${CANVAS_SHADING_NONE}" to remove it (got ${JSON.stringify(valeur)})` }),
);

/**
 * La bordure LUE : les mêmes trois mots, ou — pour une bordure que
 * `format_paragraphs` ne pose pas — ses côtés tracés, dans l'ordre de
 * {@link CANVAS_PARAGRAPH_BORDER_SIDES}, joints par `+` (`top+bottom`).
 */
export const CanvasParagraphBorderReadSchema = z
  .string()
  .regex(/^(none|box|(top|left|bottom|right)(\+(left|bottom|right)){0,2})$/, 'border must be "none", "box", or its drawn sides joined by "+" in the order top, left, bottom, right');

/**
 * Bornes de l'épaisseur d'une bordure, en points : un quart de point à douze
 * points. VALEURS DE JUGEMENT. Elles reprennent celles qu'une reproduction NON
 * OFFICIELLE de la norme donne pour l'épaisseur d'un trait — `w:sz` en
 * huitièmes de point, « with a minimum value of two (one-fourth of a point) and
 * a maximum value of 96 (twelve points) », ECMA-376 1re édition, partie 4,
 * élément `left`, lu le 2026-10-02 sur
 * https://c-rex.net/samples/ooxml/e1/Part4/OOXML_P4_DOCX_left_topic_ID0E5M6S.html.
 * Ni le document officiel ni la section de l'édition en vigueur n'ont été
 * vérifiés ; l'éditeur, lui, ne contrôle aucune borne
 * (`mesure-format-paragraphs-bordure-reset`, C6).
 */
export const CANVAS_BORDER_WIDTH_PT_MIN = 0.25;
export const CANVAS_BORDER_WIDTH_PT_MAX = 12;

export const CanvasParagraphFormatSchema = z
  .object({
    alignment: CanvasParagraphAlignmentSchema,
    indentLeftCm: z.number(),
    indentRightCm: z.number(),
    /** Négatif : retrait suspendu (la première ligne dépasse à gauche des suivantes). */
    indentFirstLineCm: z.number(),
    spaceBeforePt: z.number().nonnegative(),
    spaceAfterPt: z.number().nonnegative(),
    /**
     * Interligne, en multiple de l'interligne simple : 1 (simple), 1,15, 1,5,
     * 2 (double). Un interligne fixé en points (« Exactement », « Au moins »
     * dans Word) ne s'exprime pas ainsi : il est rendu non lisible.
     */
    lineSpacing: z.number().positive(),
    /** Les côtés où le paragraphe porte un trait : voir {@link CanvasParagraphBorderReadSchema}. */
    border: CanvasParagraphBorderReadSchema,
    /**
     * Épaisseur des traits, en points, quand tous les côtés tracés ont la même ;
     * sinon elle est rendue non lisible. Sans bordure, la lecture ne la publie
     * pas.
     */
    borderWidthPt: z.number().nonnegative(),
  })
  .strict();
export type CanvasParagraphFormat = z.infer<typeof CanvasParagraphFormatSchema>;

/**
 * D'où vient un réglage. `direct` : forme directe, posée à la main sur le
 * paragraphe ou le passage. `style` : un style nommé — de paragraphe, de
 * caractère ou de tableau, ou un style dont il hérite — ou un niveau de liste.
 * `default` : les défauts du document.
 */
export const CanvasFormatOriginSchema = z.enum(['direct', 'style', 'default']);
export type CanvasFormatOrigin = z.infer<typeof CanvasFormatOriginSchema>;

/**
 * Les réglages rangés par origine : chaque réglage figure dans UN seul des
 * trois compartiments, ou dans `unreadable`. La forme affichée est la réunion
 * des trois.
 *
 * WHY par compartiment plutôt qu'un couple valeur/origine par réglage : le
 * reçu part au modèle en JSON brut, et répéter `value`/`origin` pour chaque
 * réglage de chaque passage doublait son poids sans rien dire de plus.
 *
 * `unreadable` nomme les réglages que le lecteur ne sait pas résoudre (style
 * absent du fichier, liste introuvable, formats de tableau en désaccord…). Un réglage non
 * résolu n'est jamais deviné : son absence des compartiments n'est PAS un
 * « non ».
 */
function reglagesParOrigine<Forme extends z.ZodRawShape, Cle extends string>(
  forme: z.ZodObject<Forme, 'strict'>,
  cles: readonly [Cle, ...Cle[]],
) {
  return {
    direct: forme.partial().optional(),
    style: forme.partial().optional(),
    default: forme.partial().optional(),
    unreadable: z.array(z.enum(cles)).min(1).optional(),
  };
}

export const CanvasRunFormatReadSchema = z
  .object({
    /** Début du passage, borné à {@link CANVAS_FORMAT_READ_RUN_TEXT_MAX} caractères. */
    text: z.string().min(1).max(CANVAS_FORMAT_READ_RUN_TEXT_MAX),
    /** Longueur réelle du passage, publiée seulement quand `text` est coupé. */
    textLength: z.number().int().positive().optional(),
    ...reglagesParOrigine(CanvasRunFormatSchema, CANVAS_RUN_FORMAT_KEYS),
  })
  .strict();
export type CanvasRunFormatRead = z.infer<typeof CanvasRunFormatReadSchema>;

/** Un champ de numéro de page : le numéro de la page (`PAGE`), ou le nombre de pages (`NUMPAGES`). */
export const CanvasPageNumberFieldSchema = z.enum(['page', 'pages']);
export type CanvasPageNumberField = z.infer<typeof CanvasPageNumberFieldSchema>;

/** Les quatre marges de page, dans les mots des réglages de forme (`CANVAS_PARAGRAPH_BORDER_SIDES`). */
export const CANVAS_PAGE_MARGIN_SIDES = ['top', 'bottom', 'left', 'right'] as const;

/**
 * Nombre maximal de champs de numéro de page rendus, et retirés, pour UN paragraphe d'en-tête ou de pied. Valeur de
 * jugement : un pied en porte deux (« Page X / Y »). La lecture coupe à cette borne ; le retrait s'y arrête sur un
 * refus nommé (`HEADER_FOOTER_TOO_MANY_FIELDS`), jamais sur un succès.
 */
export const CANVAS_PARAGRAPH_FIELDS_MAX = 20;

export type CanvasPageMarginSide = (typeof CANVAS_PAGE_MARGIN_SIDES)[number];

/**
 * Les formats de page nommés. `set_page_layout` écrit leurs DIMENSIONS
 * (`w:pgSz w:w` / `w:h`) : le nom seul, passé à l'éditeur, n'écrit qu'un code et
 * laisse la page à sa taille (mesuré le 2026-10-06, `mesure-socle-page-et-defauts`).
 */
export const CanvasPaperSizeSchema = z.enum(['A3', 'A4', 'A5']);
export type CanvasPaperSize = z.infer<typeof CanvasPaperSizeSchema>;

export const CanvasPageOrientationSchema = z.enum(['portrait', 'landscape']);
export type CanvasPageOrientation = z.infer<typeof CanvasPageOrientationSchema>;

/** Sections dont une lecture de forme rend la page : au-delà, un document n'est plus un acte. */
export const CANVAS_FORMAT_READ_SECTIONS_MAX = 20;

const CentimetresLusSchema = z.number().nullable();

/**
 * La page d'une section, lue dans le fichier exporté (`w:sectPr`) : ce que
 * `set_page_layout` règle. `null` : le fichier n'écrit pas la valeur.
 * `paperSize` n'est rendu que pour un format que le verbe sait nommer.
 */
export const CanvasPageReadSchema = z
  .object({
    paperSize: CanvasPaperSizeSchema.optional(),
    widthCm: CentimetresLusSchema,
    heightCm: CentimetresLusSchema,
    orientation: CanvasPageOrientationSchema.nullable(),
    marginsCm: z.object({ top: CentimetresLusSchema, bottom: CentimetresLusSchema, left: CentimetresLusSchema, right: CentimetresLusSchema }).strict(),
  })
  .strict();
export type CanvasPageRead = z.infer<typeof CanvasPageReadSchema>;

/**
 * Ce qu'un paragraphe porte et qui n'est pas du texte ordinaire, tel que le
 * lecteur de forme le relève dans le fichier. Les verbes qui changent un
 * PASSAGE (`replace_text`, étapes `text_rewrite` d'`apply_edit_plan`) le
 * gardent ; réécrire le paragraphe ENTIER (`rewrite_block`) ne le garde pas —
 * relevé du 2026-10-09, §17.6 de `superdoc-document-api.md` :
 * - `footnote_reference`, `endnote_reference` : l'appel est retiré, la note avec lui ;
 * - `field` : le champ (date, numéro de page, renvoi) part en fin de paragraphe, vidé de son résultat ;
 * - `hyperlink` : le lien reste, vide, en fin de paragraphe — le texte n'est plus lié ;
 * - `bookmark` : un signet qui entoure du texte se retrouve vide, en fin de paragraphe ;
 * - `line_break` : le saut de ligne manuel est retiré, et un texte ne peut pas en porter ;
 * - `tab` : la tabulation est retirée, sauf si le texte neuf la porte (`\t`) ;
 * - `image_in_text` : une image suivie de texte — l'éditeur refuse l'écriture ;
 * - `pending_tracked_change` : une insertion ou une suppression en attente disparaît sans décision ;
 * - `comment_anchor` : l'ancre du commentaire part en fin de paragraphe, le commentaire reste.
 */
export const CANVAS_PARAGRAPH_CONTENT_KINDS = [
  'footnote_reference',
  'endnote_reference',
  'field',
  'hyperlink',
  'bookmark',
  'line_break',
  'tab',
  'image_in_text',
  'pending_tracked_change',
  'comment_anchor',
] as const;
export const CanvasParagraphContentKindSchema = z.enum(CANVAS_PARAGRAPH_CONTENT_KINDS);
export type CanvasParagraphContentKind = z.infer<typeof CanvasParagraphContentKindSchema>;

export const CanvasParagraphFormatReadSchema = z
  .object({
    nodeId: z.string().min(1),
    /**
     * Le lieu du paragraphe, lu hors du corps : la zone de son en-tête ou de son
     * pied, ou sa note par son `note_id` — tel quel, le `in` d'une cible qui
     * le vise. Absent : un paragraphe du corps. Son `nodeId` n'entre alors pas
     * dans une liste ni dans une plage, qui ne nomment que le corps.
     */
    in: CanvasFormatPlaceSchema.optional(),
    /** Style nommé du paragraphe ; sans style posé, c'est le style par défaut du document. `null` : aucun style lisible. */
    styleId: z.string().min(1).nullable(),
    styleName: z.string().min(1).nullable(),
    /** Literal marker and whitespace carried in paragraph text, independent of native Word numbering. */
    textListPrefix: CanvasTextListPrefixSchema.optional(),
    paragraph: z.object(reglagesParOrigine(CanvasParagraphFormatSchema, CANVAS_PARAGRAPH_FORMAT_KEYS)).strict(),
    /** Passages de texte, dans l'ordre ; un passage = une suite de texte de forme uniforme. Vide pour un paragraphe vide. */
    runs: z.array(CanvasRunFormatReadSchema),
    /**
     * Les champs de numéro de page du paragraphe, dans l'ordre du texte. `runs`
     * ne les rend pas : un pied « Page X / Y » s'y lit « Page  / ». Champ absent :
     * le paragraphe n'en porte pas.
     */
    pageNumbers: z.array(CanvasPageNumberFieldSchema).min(1).max(CANVAS_PARAGRAPH_FIELDS_MAX).optional(),
    /**
     * Ce que le paragraphe porte hors de son texte ({@link CANVAS_PARAGRAPH_CONTENT_KINDS}) : son texte se change par
     * passages, pas par une réécriture entière. Champ absent : il ne porte que du texte.
     */
    carries: z.array(CanvasParagraphContentKindSchema).min(1).max(CANVAS_PARAGRAPH_CONTENT_KINDS.length).optional(),
  })
  .strict();
export type CanvasParagraphFormatRead = z.infer<typeof CanvasParagraphFormatReadSchema>;

/**
 * Pourquoi un identifiant demandé n'a pas de forme rendue. `table_cell` : le
 * paragraphe est dans une cellule de tableau d'un en-tête, d'un pied ou d'une
 * note, hors de portée des outils de forme (dans le corps, une cellule se lit et
 * s'écrit comme un autre paragraphe). `not_found` : aucun paragraphe du corps ne porte cet identifiant
 * (identifiant périmé, inventé, ou bloc qui n'est pas un paragraphe).
 * `duplicate_node_id` : le fichier donne cet identifiant à deux paragraphes du
 * corps, cellules de tableau comprises ; les verbes de forme retrouvent un
 * paragraphe par son identifiant et ne sauraient pas lequel écrire ni juger.
 */
export const CanvasFormatUnresolvedReasonSchema = z.enum(['not_found', 'table_cell', 'duplicate_node_id']);
export type CanvasFormatUnresolvedReason = z.infer<typeof CanvasFormatUnresolvedReasonSchema>;

/**
 * Pourquoi un paragraphe COUVERT par une plage ou par « tout l'acte » n'a pas
 * de forme rendue. `table_cell` : paragraphe de cellule de tableau d'un en-tête,
 * d'un pied ou d'une note, non lu (dans le corps, une cellule est rendue).
 * `no_node_id` : le fichier ne lui donne pas d'identifiant, il ne peut être ni
 * nommé ni visé. `duplicate_node_id` : dans un lieu fait de plusieurs parties
 * (toutes les notes, une zone d'en-tête tenue par plusieurs sections), le
 * fichier lui donne un identifiant qu'un autre paragraphe du même lieu porte
 * déjà ; les verbes de forme retrouvent un paragraphe par son identifiant et
 * ne sauraient pas distinguer les deux.
 */
export const CanvasFormatSkippedReasonSchema = z.enum(['table_cell', 'no_node_id', 'duplicate_node_id']);
export type CanvasFormatSkippedReason = z.infer<typeof CanvasFormatSkippedReasonSchema>;

/**
 * Nombre de styles de paragraphe qu'une lecture de forme rend avec ce qu'ils
 * règlent, et nombre de ceux qu'elle rend ensuite par leur seul identifiant ;
 * au-delà, ils sont comptés (`paragraphStylesNotListed`). VALEURS DE JUGEMENT,
 * sur une mesure : un style rendu avec ce qu'il règle pèse 55 tokens en moyenne
 * sur le gabarit Hector, qui en définit 48 (2 637 tokens pour tous ; relevé du
 * 2026-10-06, encodeur `cl100k_base`). Les styles que l'acte emploie viennent
 * en tête. La liste ne part qu'avec la lecture de tout l'acte.
 */
export const CANVAS_FORMAT_READ_STYLES_MAX = 20;
export const CANVAS_FORMAT_READ_STYLE_IDS_MAX = 100;

/**
 * Un style de paragraphe que le fichier de l'acte définit, tel que la lecture
 * de forme le rend : ce qu'il faut pour le nommer à l'avocat et le poser
 * (`format_paragraphs`, `styleId`).
 *
 * `paragraph` et `run` portent ce que le style RÈGLE — lui ou un style dont il
 * hérite —, dans les clés et les unités de la lecture d'un paragraphe : ce que
 * montre un paragraphe qui le porte et ne pose rien à la main. Un réglage
 * absent n'est pas réglé par le style. `unreadable` nomme ceux dont la valeur,
 * sous ce style, ne se lit pas.
 */
export const CanvasParagraphStyleReadSchema = z
  .object({
    styleId: z.string().min(1),
    /** Le nom que Word montre à l'avocat. `null` : le fichier n'en donne pas. */
    styleName: z.string().min(1).nullable(),
    /** Identifiant du style dont il hérite. Champ absent : aucun. */
    basedOn: z.string().min(1).optional(),
    /** Présent quand le style numérote les paragraphes qui le portent. Champ absent : il ne numérote pas. */
    numbered: z.literal(true).optional(),
    paragraph: CanvasParagraphFormatSchema.partial().optional(),
    run: CanvasRunFormatSchema.partial().optional(),
    unreadable: z.array(z.enum([...CANVAS_PARAGRAPH_FORMAT_KEYS, ...CANVAS_RUN_FORMAT_KEYS])).min(1).optional(),
  })
  .strict();
export type CanvasParagraphStyleRead = z.infer<typeof CanvasParagraphStyleReadSchema>;

export const CanvasFormatReadSchema = z
  .object({
    paragraphs: z.array(CanvasParagraphFormatReadSchema).max(CANVAS_FORMAT_READ_PARAGRAPH_MAX),
    /**
     * Nombre de paragraphes LISIBLES que la cible désigne dans l'acte entier,
     * au-delà de la borne d'une lecture comprise. Ceux que la cible couvre sans
     * qu'ils soient lisibles sont comptés dans `skipped`, jamais ici.
     */
    matchCount: z.number().int().nonnegative(),
    /**
     * Paragraphes couverts par la cible et non rendus, comptés par raison. Un
     * paragraphe visé ne disparaît jamais en silence : sans ce compte, un lieu
     * se lirait comme complet alors qu'un paragraphe sans identifiant, ou le
     * tableau d'un en-tête, d'un pied ou d'une note, a été laissé de côté (les
     * cellules de tableau du corps, elles, sont rendues). Champ absent : la
     * cible ne couvre que des paragraphes lisibles.
     */
    skipped: z
      .array(z.object({ reason: CanvasFormatSkippedReasonSchema, count: z.number().int().positive() }).strict())
      .min(1)
      .optional(),
    /**
     * Identifiants demandés sans forme rendue, avec la raison. Champ absent :
     * tous les identifiants demandés ont été trouvés.
     */
    unresolved: z
      .array(z.object({ nodeId: z.string().min(1), reason: CanvasFormatUnresolvedReasonSchema }).strict())
      .min(1)
      .optional(),
    /**
     * Cible à repasser telle quelle dans `formatOf` pour lire la suite, quand
     * la cible désigne plus de {@link CANVAS_FORMAT_READ_PARAGRAPH_MAX}
     * paragraphes. Champ absent : la lecture est complète.
     */
    continuation: CanvasParagraphTargetSchema.optional(),
    /**
     * Les styles de paragraphe que le fichier définit, ceux que les paragraphes
     * du corps portent en tête (du plus porté au moins porté), puis les autres
     * dans l'ordre du fichier. Ils ne dépendent pas de la cible lue : ils sont
     * émis avec la seule lecture de tout l'acte (`formatOf` `all`), que sa
     * suite — une plage — ne répète pas. Champ absent : une autre lecture, ou
     * un navigateur resté sur un bundle antérieur.
     */
    paragraphStyles: z.array(CanvasParagraphStyleReadSchema).max(CANVAS_FORMAT_READ_STYLES_MAX).optional(),
    /**
     * Les identifiants des styles de paragraphe définis qui suivent ceux de
     * `paragraphStyles`, dans le même ordre : tout style que le fichier définit
     * reste ainsi posable, même quand ce qu'il règle n'est pas rendu. Champ
     * absent : `paragraphStyles` les rend tous, ou n'est pas émis.
     */
    otherParagraphStyleIds: z.array(z.string().min(1)).min(1).max(CANVAS_FORMAT_READ_STYLE_IDS_MAX).optional(),
    /** Nombre de styles de paragraphe définis que ni `paragraphStyles` ni `otherParagraphStyleIds` ne rendent. Champ absent : tous y sont, ou `paragraphStyles` n'est pas émis. */
    paragraphStylesNotListed: z.number().int().positive().optional(),
    /**
     * La page de chaque section, dans l'ordre du document. Comme les styles,
     * elle ne dépend pas de la cible : émise avec la seule lecture de tout
     * l'acte. Champ absent : une autre lecture, ou un fichier sans section lue.
     */
    page: z.array(CanvasPageReadSchema).min(1).max(CANVAS_FORMAT_READ_SECTIONS_MAX).optional(),
  })
  .strict();
export type CanvasFormatRead = z.infer<typeof CanvasFormatReadSchema>;

/**
 * Ce que `canvas_inspect` reçoit pour rendre la lecture avec forme de l'acte
 * ouvert (`formatReading`) : le rang où commencer, comme `read_document_format`.
 */
export const CanvasFormatReadingRequestSchema = z
  .object({
    start_paragraph: z.number().int().positive().describe('Rang du premier paragraphe à lire, à partir de 1.'),
  })
  .strict();
export type CanvasFormatReadingRequest = z.infer<typeof CanvasFormatReadingRequestSchema>;

/**
 * La lecture avec forme de l'acte ouvert, telle que la rend `lireAvecForme`
 * (`@hector/docx-forme`) avec l'identifiant de bloc de chaque paragraphe.
 *
 * WHY les mêmes noms de champ que le résultat de `read_document_format`
 * (backend, `tools/backend/ReadDocumentFormat.ts`) : l'agent met la lecture de
 * l'acte en regard de celle d'un modèle, ligne à ligne ; deux formes de
 * résultat pour le même texte l'obligeraient à traduire avant de comparer.
 */
export const CanvasFormatReadingSchema = z
  .object({
    format_reading: z.string().min(1),
    /** Paragraphes non vides du corps, cellules de tableau comprises. */
    total_paragraphs: z.number().int().nonnegative(),
    /** Rang du premier paragraphe rendu ; 0 quand l'acte n'en a aucun. */
    start_paragraph: z.number().int().nonnegative(),
    end_paragraph: z.number().int().nonnegative(),
    /** Rang où reprendre, `null` quand tout est rendu. */
    next_start_paragraph: z.number().int().positive().nullable(),
  })
  .strict();
export type CanvasFormatReading = z.infer<typeof CanvasFormatReadingSchema>;

/**
 * Nombre maximal d'identifiants qu'une page de lecture avec forme arme.
 *
 * WHY 3 000 : une page rend au plus 60 000 caractères (`TAILLE_DE_PAGE_DE_LECTURE`,
 * `@hector/docx-forme`, recopié et non importé : ce paquet en dépend) et la plus
 * courte ligne à identifiant en compte une vingtaine (`[1 · id 0A] {centré} x`).
 * La page ne peut donc pas l'atteindre : c'est un garde-fou de trame, pas une
 * pagination. Le plafond de 250 d'une lecture globale
 * (`CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX`) borne ce qui part au MODÈLE ; cette
 * liste n'y part pas, et chaque identifiant qu'une ligne montre doit être armé.
 */
export const CANVAS_FORMAT_READING_SCOPE_NODE_IDS_MAX = 3000;

/**
 * Ce qu'une lecture avec forme ARME pour les écritures de texte : les
 * identifiants des paragraphes du corps que sa page a rendus, à la révision où
 * le fichier a été lu.
 *
 * WHY : mesuré dans la vraie application le 2026-10-12, deux passages. L'agent
 * devait recomposer la page de garde d'un acte sur celle d'un modèle (avocat sur
 * trois lignes, blocs ajoutés ou déplacés) : il lisait le texte entier et
 * l'identifiant de chaque paragraphe, et ne faisait aucune écriture de texte,
 * la lecture n'armant que les verbes de forme.
 *
 * WHY hors de `formatReading` : ce reçu-là part au modèle en JSON brut, et les
 * identifiants y sont déjà, dans le texte. La liste voyage sur l'enveloppe du
 * résultat (`canvasResultSuccessSchema`), le courtier la lit, puis la vue du
 * modèle la retire (`canvasReadMarkers.ts`, backend) : coût nul pour le modèle.
 *
 * N'y figure que ce qu'un verbe de contenu adresse : un paragraphe du corps
 * (une cellule de tableau se change par `replace_text`), à l'identifiant
 * unique, que la projection finale de l'éditeur publie à cette révision.
 */
export const CanvasFormatReadingScopeSchema = z
  .object({
    /** La révision du reçu : celle où le fichier lu a été clôturé. */
    revision: z.string().min(1),
    nodeIds: z.array(z.string().min(1)).max(CANVAS_FORMAT_READING_SCOPE_NODE_IDS_MAX),
  })
  .strict();
export type CanvasFormatReadingScope = z.infer<typeof CanvasFormatReadingScopeSchema>;

// ── Reçu d'une écriture de forme (#1863, spec #1858, règle commune n°4) ──────

/**
 * Réglages de texte que `format_text` sait écrire. L'ordre est celui du reçu.
 * Chacun est relu par le lecteur de forme : un réglage n'entre ici qu'avec sa
 * lecture, et sa clé est celle sous laquelle le lecteur le rend
 * ({@link CANVAS_RUN_FORMAT_KEYS}).
 */
export const CANVAS_TEXT_FORMAT_SETTING_KEYS = [
  'bold',
  'italic',
  'underline',
  'highlight',
  'color',
  'fontFamily',
  'fontSizePt',
] as const satisfies readonly CanvasRunFormatKey[];
export type CanvasTextFormatSettingKey = (typeof CANVAS_TEXT_FORMAT_SETTING_KEYS)[number];

/**
 * Tout réglage qu'un reçu d'écriture de forme peut nommer : de texte
 * (`format_text`) ou de paragraphe (`format_paragraphs`, #1866, qui écrit tous
 * les réglages que le lecteur de forme rend pour un paragraphe, sous les mêmes
 * clés et dans les mêmes unités), ou `reset` — la remise à zéro de la forme
 * posée à la main (#1867), qui n'est pas un réglage lu : elle est jugée sur
 * l'origine de la forme, et son reçu porte `requested: true` —, ou `styleId` :
 * le style nommé que `format_paragraphs` pose, ou celui qu'une copie de forme
 * (`copy_format`, #1869) pose sur ses cibles — `requested` est l'identifiant
 * du style demandé, ou de celui du paragraphe modèle —, ou
 * `otherTextFormatting` : toute forme de caractère posée à la main hors des
 * sept réglages de texte (barré, capitales, exposant, ombrage…), qu'une copie
 * retire des cibles ; `requested` vaut `none`, et la valeur lue nomme les
 * éléments du fichier encore là —, ou un fond ({@link CANVAS_SHADING_SETTING_KEYS}) :
 * `requested` et la valeur lue sont de la forme de {@link fondNormalise} —, ou
 * le cadre d'une case de tableau ({@link CANVAS_CELL_BORDER_SETTING_KEY}), dans
 * les mots de `border`.
 */
export const CANVAS_FORMAT_SETTING_KEYS = [...CANVAS_TEXT_FORMAT_SETTING_KEYS, ...CANVAS_PARAGRAPH_FORMAT_KEYS, ...CANVAS_SHADING_SETTING_KEYS, CANVAS_CELL_BORDER_SETTING_KEY, 'reset', 'styleId', 'otherTextFormatting'] as const;
export type CanvasFormatSettingKey = (typeof CANVAS_FORMAT_SETTING_KEYS)[number];

/**
 * Profondeur de l'historique d'annulation de l'éditeur : il garde ses 100
 * dernières écritures, et aucune annulation ne défait les plus anciennes.
 * Mesuré sur SuperDoc 2.15.1 (`mesure-format-paragraphs`, 2026-10-02 : 130
 * écritures, 100 annulations abouties, les 30 premières écritures restent).
 * C'est le plafond d'`undo_changes`, et ce qui borne ce qu'un reçu d'écriture
 * de forme dit défaisable.
 */
export const CANVAS_UNDO_HISTORY_DEPTH = 100;

/**
 * Le mot qui RETIRE un réglage de texte qui n'est pas un booléen (surlignage,
 * couleur, police, taille). Un booléen se retire par `false`.
 */
export const CANVAS_FORMAT_REMOVE = 'none';

/**
 * Dans un FILTRE (`onlyWhere` de `format_text`, #1865), le mot qui dit « a ce
 * réglage, quelle qu'en soit la valeur » : un surlignage de n'importe quelle
 * couleur, une couleur de texte autre que le noir. Son contraire est
 * {@link CANVAS_FORMAT_REMOVE}. Ce n'est jamais une valeur à écrire.
 */
export const CANVAS_FORMAT_ANY = 'any';

/**
 * Valeur d'un réglage : demandée par l'agent, ou relue dans le fichier. Un
 * nombre est dans l'unité que porte le nom du réglage (`…Pt` : points, `…Cm` :
 * centimètres, `lineSpacing` : multiple de l'interligne simple).
 */
const CanvasFormatSettingValueSchema = z.union([z.boolean(), z.string().min(1), z.number()]);
export type CanvasFormatSettingValue = z.infer<typeof CanvasFormatSettingValueSchema>;

/**
 * Ce que la relecture a établi pour UN réglage. `conforming` : chaque passage
 * écrit porte la valeur demandée. `different` : au moins un passage en porte
 * une autre. `unreadable` : aucun écart lu, mais au moins un passage n'a pas pu
 * être jugé (non relu, ou non écrit) — ce n'est ni un « oui » ni un « non ».
 * Pour un réglage de paragraphe, l'unité jugée et comptée est le paragraphe.
 */
export const CanvasFormatSettingOutcomeSchema = z.enum(['conforming', 'different', 'unreadable']);
export type CanvasFormatSettingOutcome = z.infer<typeof CanvasFormatSettingOutcomeSchema>;

/**
 * Pourquoi un réglage n'a pas été jugé sur un passage. `cascade_not_resolved` :
 * sa valeur dépend d'une part du fichier que le lecteur ne résout pas (style de
 * caractère, style introuvable). `target_not_found_on_reread` : le paragraphe
 * ou le passage écrit n'est plus dans le fichier relu. `reread_failed` : le
 * fichier n'a pas pu être exporté ou lu après l'écriture.
 * `changed_during_reread` : le document a changé pendant la relecture (l'avocat
 * tape), le fichier relu n'est donc plus celui de l'écriture.
 * `revision_unavailable` : l'éditeur n'a pas rendu la révision du document
 * après l'écriture, rien ne prouve donc que le fichier relu est celui de
 * l'écriture. `not_written` : le paragraphe visé n'a pas été écrit (plan
 * refusé après d'autres plans réussis, ou paragraphe que l'éditeur refuse
 * d'écrire et que `skipped` nomme), il n'y a donc rien à relire.
 * `attributes_would_be_lost` : ce réglage n'a pas été écrit sur le paragraphe,
 * parce que l'écrire aurait effacé des attributs posés à la main que l'éditeur
 * ne sait pas redonner ; le paragraphe garde, pour ce réglage, sa forme d'avant.
 * `pending_tracked_change` : le réglage n'a pas été écrit sur le paragraphe,
 * parce qu'il porte une révision en attente — que la remise à zéro ferait
 * disparaître (forme de paragraphe, marque de paragraphe insérée ou supprimée),
 * ou sur laquelle l'éditeur refuse d'écrire une forme de texte (forme de texte,
 * déplacement). Le même mot que la raison de saut
 * ({@link CanvasFormatEditorRefusalReasonSchema}). `outline_level_not_restorable` : le paragraphe n'a pas été
 * remis à zéro, parce qu'il porte un niveau de plan que l'éditeur ne saurait
 * pas redonner ensuite (hors des entiers 0 à 9). `list_numbering` : une copie de
 * forme n'a pas écrit ce réglage sur le paragraphe, parce qu'il tient à sa
 * numérotation, qu'une copie ne change pas — les retraits d'un paragraphe de
 * liste, ou le style nommé d'un paragraphe que son style numérote.
 * `table_cell_not_reread` : l'étape de forme d'un plan (`apply_edit_plan`)
 * visait un tableau, et l'éditeur n'a pas dit dans quel paragraphe de cellule il
 * a écrit le passage : rien ne dit où le relire, la forme écrite n'est ni
 * confirmée ni infirmée. Un passage en cellule dont le paragraphe est connu est
 * relu comme un autre.
 */
export const CanvasFormatUnreadableReasonSchema = z.enum([
  'cascade_not_resolved',
  'target_not_found_on_reread',
  'reread_failed',
  'changed_during_reread',
  'revision_unavailable',
  'not_written',
  'attributes_would_be_lost',
  'pending_tracked_change',
  'outline_level_not_restorable',
  'list_numbering',
  'table_cell_not_reread',
]);
export type CanvasFormatUnreadableReason = z.infer<typeof CanvasFormatUnreadableReasonSchema>;

/** Nombre maximal de passages cités en exemple pour un réglage « différent ». */
export const CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX = 5;
/** Longueur maximale du texte d'un passage cité en exemple. */
export const CANVAS_FORMAT_DIFFERENCE_TEXT_MAX = 60;
/** Nombre maximal de formes distinctes nommées dans `remaining`, les plus fréquentes d'abord. */
export const CANVAS_FORMAT_REMAINING_MAX = 5;

/**
 * Ce qui reste après le RETRAIT d'un réglage de texte, compté en passages :
 * voir `remaining` de {@link CanvasFormatSettingReceiptSchema}.
 */
const CanvasFormatRemainingTextSchema = z
  .object({
    /** La valeur lue. Absente : elle ne se lit pas (police de thème sans nom lisible, style absent du fichier). */
    read: CanvasFormatSettingValueSchema.optional(),
    /**
     * D'où vient ce qui reste. `inherited` : pas de la main — du style ou des
     * défauts du document, sans que le lecteur sache lequel (c'est l'origine
     * d'une valeur qui ne se lit pas).
     */
    origin: z.enum([...CanvasFormatOriginSchema.options, 'inherited']),
    /**
     * Le style nommé du PARAGRAPHE du passage, rendu quand l'origine est
     * `style`. C'est le plus souvent lui qui donne la valeur ; ce peut aussi
     * être un style de caractère, un style de tableau ou un niveau de liste,
     * que le reçu ne nomme pas.
     */
    paragraphStyleName: z.string().min(1).optional(),
    /** Nombre de PASSAGES de texte qui portent cette forme — pas de paragraphes. */
    passages: z.number().int().positive(),
  })
  .strict();

/**
 * Ce que des paragraphes montrent après une REMISE À ZÉRO (`reset`), compté en
 * paragraphes : la forme de paragraphe rendue, et le style nommé qui la donne.
 */
const CanvasFormatRemainingParagraphSchema = z
  .object({
    /** `style` : le paragraphe a un style nommé (`paragraphStyleName`) ; `default` : il n'en a pas. */
    origin: z.enum(['style', 'default']),
    paragraphStyleName: z.string().min(1).optional(),
    /** La forme de paragraphe que le style nommé et les défauts donnent, réglages non lisibles omis. */
    paragraph: CanvasParagraphFormatSchema.partial(),
    /** Nombre de PARAGRAPHES qui montrent cette forme. */
    paragraphs: z.number().int().positive(),
  })
  .strict();

export const CanvasFormatSettingReceiptSchema = z
  .object({
    setting: z.enum(CANVAS_FORMAT_SETTING_KEYS),
    requested: CanvasFormatSettingValueSchema,
    outcome: CanvasFormatSettingOutcomeSchema,
    /** Passages relus avec une autre valeur : leur nombre, et les premiers avec la valeur lue. */
    different: z
      .object({
        count: z.number().int().positive(),
        examples: z
          .array(
            z
              .object({
                nodeId: z.string().min(1),
                /**
                 * Début du passage relu — du paragraphe, pour un réglage de
                 * paragraphe — borné à {@link CANVAS_FORMAT_DIFFERENCE_TEXT_MAX}
                 * caractères. Absent : paragraphe sans texte.
                 */
                text: z.string().min(1).max(CANVAS_FORMAT_DIFFERENCE_TEXT_MAX).optional(),
                read: CanvasFormatSettingValueSchema,
              })
              .strict(),
          )
          .min(1)
          .max(CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX),
      })
      .strict()
      .optional(),
    /** Passages non jugés : leur nombre, et la première raison rencontrée. */
    unreadable: z
      .object({ count: z.number().int().positive(), reason: CanvasFormatUnreadableReasonSchema })
      .strict()
      .optional(),
    /**
     * Après un RETRAIT, ce que le texte porte encore là où la demande est
     * satisfaite : la police ou la taille que donne le style du paragraphe
     * (« le texte reste en Arial 16, paragraphe au style Titre »), ou le surlignage
     * blanc posé à la main contre le surlignage d'un style, que l'éditeur ne
     * sait pas retirer, ou le noir d'un style. Une entrée par forme distincte ;
     * au plus {@link CANVAS_FORMAT_REMAINING_MAX}, les plus fréquentes d'abord
     * (`remainingNotListed` compte les autres). Absent : rien ne reste.
     *
     * Après une REMISE À ZÉRO (`reset`), ce que les paragraphes montrent
     * désormais : une entrée par forme de paragraphe distincte, avec le style
     * nommé qui la donne. La forme du texte n'y est pas : elle se lit par
     * passage (`formatOf`).
     */
    remaining: z
      .array(z.union([CanvasFormatRemainingTextSchema, CanvasFormatRemainingParagraphSchema]))
      .min(1)
      .max(CANVAS_FORMAT_REMAINING_MAX)
      .optional(),
    /** Nombre de FORMES distinctes que `remaining` ne liste pas, au-delà de sa borne. Absent : toutes y sont. */
    remainingNotListed: z.number().int().positive().optional(),
  })
  .strict()
  // WHY un contrôle croisé : `different` sans valeur lue, ou `conforming` avec
  // un écart, laisserait le modèle choisir lequel des deux champs croire.
  .superRefine((reglage, ctx) => {
    const attendu = {
      conforming: { different: false, unreadable: false },
      different: { different: true, unreadable: reglage.unreadable !== undefined },
      unreadable: { different: false, unreadable: true },
    }[reglage.outcome];
    const remiseAZero = reglage.setting === 'reset';
    if (reglage.remaining !== undefined && reglage.requested !== CANVAS_FORMAT_REMOVE && !remiseAZero) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format setting ${reglage.setting}: "remaining" describes what is left after a removal, `
          + `so it requires requested "${CANVAS_FORMAT_REMOVE}" or the setting "reset" (got ${JSON.stringify(reglage.requested)}).`,
      });
    }
    // WHY : `reset` compte des paragraphes, le retrait d'un réglage de texte des passages — jamais l'inverse.
    for (const reste of reglage.remaining ?? []) {
      if (('paragraphs' in reste) !== remiseAZero) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `format setting ${reglage.setting}: a "remaining" entry counts ${remiseAZero ? 'paragraphs ("paragraph", "paragraphs")' : 'passages ("passages")'}; got ${JSON.stringify(reste)}.`,
        });
      }
    }
    if (reglage.remainingNotListed !== undefined && reglage.remaining?.length !== CANVAS_FORMAT_REMAINING_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format setting ${reglage.setting}: "remainingNotListed" counts the forms beyond a FULL "remaining" list `
          + `(${CANVAS_FORMAT_REMAINING_MAX} entries), got ${reglage.remaining?.length ?? 'no'} entries.`,
      });
    }
    if ((reglage.different !== undefined) !== attendu.different || (reglage.unreadable !== undefined) !== attendu.unreadable) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format setting ${reglage.setting}: outcome "${reglage.outcome}" requires `
          + '"different" exactly when a different value was read, and "unreadable" exactly when a passage could not be re-read '
          + '(conforming carries neither; unreadable carries no "different").',
      });
    }
  });
export type CanvasFormatSettingReceipt = z.infer<typeof CanvasFormatSettingReceiptSchema>;

/**
 * Pourquoi un paragraphe couvert par la cible n'a pas été écrit. Aux raisons
 * de la lecture s'ajoutent `no_text` — un paragraphe sans texte n'a pas de
 * texte à mettre en forme (`format_text`) — et `attributes_would_be_lost` :
 * le paragraphe porte, posés à la main, des attributs de retrait ou
 * d'espacement que l'éditeur efface en écrivant cet élément (espacement
 * automatique, valeurs en lignes ou en caractères) ; les réglages demandés qui
 * passent par cet élément n'y sont pas écrits (`format_paragraphs`). Les
 * autres le sont : `settings` dit, réglage par réglage, lesquels sont laissés.
 * `outline_level_not_restorable` : il porte un niveau de plan que l'éditeur ne
 * saurait pas redonner après une remise à zéro, qui n'est donc pas faite.
 * `copy_source` : le paragraphe est le modèle d'une copie de forme
 * (`copy_format`) et se trouve dans sa propre cible ; il n'est pas écrit.
 * `table_cell_by_ids_only` : une forme de PARAGRAPHE (`format_paragraphs`,
 * `copy_format`) demandée sur « tout l'acte » ou sur une plage ne s'écrit pas
 * dans les cellules de tableau du corps — la mise en page d'une cellule
 * appartient à son tableau. Elles sont comptées, et s'écrivent quand la cible
 * les nomme (`ids`). Ce n'est pas un échec : le reçu reste `ok`.
 */
const RAISONS_DE_PARAGRAPHE_NON_ECRIT = ['table_cell', 'table_cell_by_ids_only', 'no_node_id', 'duplicate_node_id', 'no_text', 'attributes_would_be_lost', 'outline_level_not_restorable', 'copy_source'] as const;

/**
 * Pourquoi un paragraphe — ou, sous un filtre, un passage — que la cible
 * désigne n'est pas écrit à cause de ce qu'il porte (#1868). L'éditeur refuse
 * en bloc le lot qui le porte ; l'écriture est alors posée sur tout le reste,
 * et lui est sauté et nommé, avec l'une de ces raisons :
 * - `bookmark` : un signet couvre une partie de son texte ;
 * - `bookmark_across_paragraphs` : un signet commence dans un paragraphe et
 *   finit dans un autre ;
 * - `pending_tracked_change` : il porte une révision en attente — changement
 *   de forme, texte déplacé, marque de paragraphe insérée ou supprimée — sur
 *   laquelle l'éditeur refuse d'écrire, ou qu'une remise à zéro (`reset`)
 *   effacerait : dans ce second cas le paragraphe est laissé d'avance, sans
 *   que l'éditeur ait rien refusé. La seule raison du contrat pour une
 *   révision en attente ;
 * - `content_control` : il porte un contrôle de contenu (champ de modèle) ;
 * - `passage_not_addressable` : l'éditeur ne sait pas y viser un passage de
 *   texte (appel de note, texte déplacé en attente) ;
 * - `refused_by_editor` : toute autre cause, dite dans `detail` avec les mots
 *   de l'éditeur.
 */
export const CanvasFormatEditorRefusalReasonSchema = z.enum([
  'bookmark',
  'bookmark_across_paragraphs',
  'pending_tracked_change',
  'content_control',
  'passage_not_addressable',
  'refused_by_editor',
]);
export type CanvasFormatEditorRefusalReason = z.infer<typeof CanvasFormatEditorRefusalReasonSchema>;

/** Vrai pour une raison de {@link CanvasFormatEditorRefusalReasonSchema}. */
export function estUnRefusDeLEditeur(reason: string): reason is CanvasFormatEditorRefusalReason {
  return (CanvasFormatEditorRefusalReasonSchema.options as readonly string[]).includes(reason);
}

export const CanvasFormatWriteSkippedReasonSchema = z.enum([...RAISONS_DE_PARAGRAPHE_NON_ECRIT, ...CanvasFormatEditorRefusalReasonSchema.options]);
export type CanvasFormatWriteSkippedReason = z.infer<typeof CanvasFormatWriteSkippedReasonSchema>;

/**
 * Longueur maximale de la cause de l'éditeur recopiée dans un reçu (`detail`).
 * Valeur de jugement, non mesurée : assez pour une phrase de l'éditeur, trop
 * peu pour qu'un extrait de document y passe.
 */
export const CANVAS_FORMAT_REFUSAL_DETAIL_MAX = 200;

/**
 * Nombre maximal de paragraphes nommés, par groupe, pour ce qu'une écriture de
 * forme n'a pas écrit : paragraphes sautés (`skipped`) comme passages écartés
 * (`onlyWhere.passagesSetAside`, `passagesNotEvaluated`). La même borne vaut
 * pour les textes d'une liste visée dans tout l'acte (`everywhere.texts`).
 * Valeur de jugement, non mesurée : assez pour que l'agent les cite à l'avocat.
 */
export const CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX = 20;

/**
 * Longueur maximale d'un texte visé recopié dans un reçu ou dans un refus,
 * avant le « … » qui marque la coupe : assez pour le reconnaître. Valeur de
 * jugement.
 */
export const CANVAS_FORMAT_QUOTED_TEXT_MAX = 80;

/**
 * Pourquoi un passage trouvé par un filtre n'a pas été écrit.
 * `paragraph_text_differs` : le texte du paragraphe vu par l'éditeur n'est pas
 * celui du fichier (appel de note de bas de page ou de fin, tiret insécable),
 * donc une position prise au fichier n'y désigne pas le même texte.
 * `paragraph_text_unreadable` : l'éditeur n'a pas rendu le texte du paragraphe.
 * `position_not_proven` : à la position du passage, l'éditeur vise un autre
 * texte ou une autre plage. Les raisons de
 * {@link CanvasFormatEditorRefusalReasonSchema} : l'éditeur refuse d'écrire le
 * passage.
 */
export const CanvasFormatSetAsideReasonSchema = z.enum([
  'paragraph_text_differs',
  'paragraph_text_unreadable',
  'position_not_proven',
  // Le passage est situé, mais l'éditeur refuse de l'écrire (#1868).
  ...CanvasFormatEditorRefusalReasonSchema.options,
]);
export type CanvasFormatSetAsideReason = z.infer<typeof CanvasFormatSetAsideReasonSchema>;

/** Les paragraphes d'un groupe : nommés jusqu'à la borne, comptés au-delà. */
const paragraphesNommes = {
  /** Les paragraphes concernés, dans l'ordre du document, au plus {@link CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX}. */
  nodeIds: z.array(z.string().min(1)).min(1).max(CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX),
  /** Nombre de paragraphes concernés que `nodeIds` ne nomme pas, au-delà de sa borne. Absent : tous y sont. */
  paragraphsNotListed: z.number().int().positive().optional(),
};

/**
 * Un groupe de ce qu'une écriture de forme n'a pas écrit, pour UNE raison :
 * des paragraphes (`skipped`) ou des passages (`onlyWhere.passagesSetAside`).
 * La même forme pour les deux : leur nombre, leurs paragraphes nommés, et —
 * sous `refused_by_editor`, qu'elle seule explique — la cause que l'éditeur
 * donne, bornée (un « … » marque la coupe).
 *
 * @param raisons Les raisons que ce groupe admet.
 */
function groupeNonEcrit<Raisons extends [string, ...string[]]>(raisons: z.ZodEnum<Raisons>) {
  return z
    .object({
      reason: raisons,
      count: z.number().int().positive(),
      nodeIds: paragraphesNommes.nodeIds.optional(),
      paragraphsNotListed: paragraphesNommes.paragraphsNotListed,
      detail: z.string().min(1).max(CANVAS_FORMAT_REFUSAL_DETAIL_MAX + 1).optional(),
    })
    .strict()
    // WHY : la cause de l'éditeur n'a de sens que là où aucune raison nommée ne l'explique, et là elle est la seule explication.
    .superRefine((groupe, ctx) => {
      if ((groupe.reason === 'refused_by_editor') !== (groupe.detail !== undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['detail'],
          message: `format receipt: "detail" (the editor's own words) must be present exactly under reason "refused_by_editor"; `
            + `got reason "${String(groupe.reason)}" ${groupe.detail === undefined ? 'without' : 'with'} a detail.`,
        });
      }
    });
}

/**
 * Ce qu'un filtre `onlyWhere` a trouvé et ce qu'il en est advenu (#1865). Un
 * PASSAGE est ici la plus longue suite de texte d'un paragraphe qui a le même
 * sort face au filtre : deux mots gras qui se suivent font un passage, deux
 * mots gras séparés par un mot maigre en font deux.
 */
const CanvasFormatFilterReceiptSchema = z
  .object({
    /** Passages de la cible qui satisfont le filtre. Zéro : rien ne le satisfait, rien n'est écrit. */
    passagesFound: z.number().int().nonnegative(),
    /** Passages écrits par cet appel. */
    passagesWritten: z.number().int().nonnegative(),
    /** Passages trouvés où l'écriture n'aurait rien changé : non réécrits, jugés par la relecture avec les autres. Absent : aucun. */
    passagesAlreadyFormatted: z.number().int().positive().optional(),
    /** Passages trouvés que l'éditeur n'a PAS écrits : le plan qui les portait a été refusé après d'autres plans réussis. Absent : aucun. */
    passagesNotWritten: z.number().int().positive().optional(),
    /**
     * Passages trouvés et laissés tels quels plutôt qu'écrits au mauvais
     * endroit, groupés par raison, leurs paragraphes nommés. Absent : aucun.
     */
    passagesSetAside: z.array(groupeNonEcrit(CanvasFormatSetAsideReasonSchema)).min(1).optional(),
    /**
     * Passages de la cible où le filtre n'a pas pu être évalué : `settings`
     * nomme les réglages du filtre qui ne s'y lisent pas. Ils ne sont ni
     * trouvés ni exclus : ils ne sont pas écrits, et ne comptent pas dans
     * `passagesFound`. Absent : aucun.
     */
    passagesNotEvaluated: z
      .object({
        count: z.number().int().positive(),
        settings: z.array(z.enum(CANVAS_TEXT_FORMAT_SETTING_KEYS)).min(1),
        ...paragraphesNommes,
      })
      .strict()
      .optional(),
  })
  .strict()
  // WHY : chaque passage trouvé a UN sort. Des comptes qui ne se recoupent pas laisseraient le modèle choisir lequel croire.
  .superRefine((filtre, ctx) => {
    const ecartes = (filtre.passagesSetAside ?? []).reduce((somme, groupe) => somme + groupe.count, 0);
    const sorts = filtre.passagesWritten + (filtre.passagesAlreadyFormatted ?? 0) + (filtre.passagesNotWritten ?? 0) + ecartes;
    for (const groupe of filtre.passagesSetAside ?? []) {
      if (groupe.nodeIds === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['passagesSetAside'],
          message: `format.onlyWhere sets ${groupe.count} passage(s) aside for reason "${groupe.reason}" without naming their paragraphs: "nodeIds" is required here.`,
        });
      }
    }
    if (sorts !== filtre.passagesFound) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format.onlyWhere: passagesFound (${filtre.passagesFound}) must equal passagesWritten + passagesAlreadyFormatted `
          + `+ passagesNotWritten + the passagesSetAside counts (${sorts}).`,
      });
    }
  });

/** Un texte visé, tel qu'un reçu le recopie : coupé à {@link CANVAS_FORMAT_QUOTED_TEXT_MAX}, « … » compris. */
const texteCite = z.string().min(1).max(CANVAS_FORMAT_QUOTED_TEXT_MAX + 1);

/**
 * Le compte de chaque texte d'une LISTE visée dans tout l'acte (#1947) : un
 * texte sans aucune occurrence n'annule pas les autres, il est nommé ici.
 * Chaque liste nomme au plus {@link CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX} textes,
 * dans l'ordre donné, et compte le reste.
 */
const CanvasFormatTextsReceiptSchema = z
  .object({
    /** Textes distincts donnés. */
    given: z.number().int().min(2).max(CANVAS_FORMAT_TEXTS_MAX),
    /** Les textes qui ont au moins une occurrence en mot entier, où qu'elle soit. Absent : aucun. */
    perText: z
      .array(
        z
          .object({
            text: texteCite,
            /** Ses occurrences que l'écriture vise, dans le corps (cellules de tableau comprises) ou dans le lieu demandé : leur somme est `occurrencesFound`. */
            found: z.number().int().nonnegative(),
            /** Celles que cet appel a écrites. Absent sous un filtre `onlyWhere`, comme `occurrencesWritten`. */
            written: z.number().int().nonnegative().optional(),
            /** Ses occurrences tenues en entier dans celle d'un texte plus long de la liste : couvertes par lui. Absent : aucune. */
            insideLongerText: z.number().int().positive().optional(),
            /** Ses occurrences à cheval sur celle d'un texte plus long, sans y tenir : laissées telles quelles. Absent : aucune. */
            overlappingLongerText: z.number().int().positive().optional(),
            /** Ses occurrences hors de portée (paragraphe sans identifiant ou à l'identifiant en double ; cellule de tableau d'un en-tête, d'un pied ou d'une note). Absent : aucune. */
            outOfReach: z.number().int().positive().optional(),
          })
          .strict(),
      )
      .min(1)
      .max(CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX)
      .optional(),
    /** Nombre de textes à occurrences que `perText` ne nomme pas, au-delà de sa borne. Absent : tous y sont. */
    textsNotListed: z.number().int().positive().optional(),
    /** Les textes sans aucune occurrence en mot entier ; `insideLongerWord` : le nombre de fois où le texte n'est là que dans un mot ou un nombre plus long. Absent : aucun. */
    notFound: z
      .array(z.object({ text: texteCite, insideLongerWord: z.number().int().positive().optional() }).strict())
      .min(1)
      .max(CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX)
      .optional(),
    /** Nombre de textes introuvables que `notFound` ne nomme pas, au-delà de sa borne. Absent : tous y sont. */
    notFoundNotListed: z.number().int().positive().optional(),
  })
  .strict()
  // WHY : chaque texte donné est dans UNE des deux listes, nommé ou compté. Un texte qui n'y serait pas serait un texte dont le reçu ne dit rien.
  .superRefine((textes, ctx) => {
    const dits = (textes.perText?.length ?? 0) + (textes.textsNotListed ?? 0) + (textes.notFound?.length ?? 0) + (textes.notFoundNotListed ?? 0);
    if (dits !== textes.given) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format.everywhere.texts: given (${textes.given}) must equal the texts of perText and notFound, named or counted (${dits}).`,
      });
    }
    for (const texte of textes.perText ?? []) {
      if (texte.found + (texte.insideLongerText ?? 0) + (texte.overlappingLongerText ?? 0) + (texte.outOfReach ?? 0) === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['perText'],
          message: `format.everywhere.texts.perText names ${JSON.stringify(texte.text)} without any occurrence: a text with none belongs in "notFound".`,
        });
      }
    }
  });

/**
 * Ce qu'un texte exact visé dans TOUT l'acte a désigné, et ce qu'il en est
 * advenu (cible `{kind:"text"}` sans paragraphe, #1947). Une occurrence a UN
 * sort : écrite, déjà en forme, non écrite, ou écartée.
 *
 * Sous un filtre `onlyWhere`, ce qui s'écrit n'est plus l'occurrence mais la
 * part de son texte que le filtre retient : les sorts sont alors ceux de
 * `onlyWhere`, et seuls `occurrencesFound` et `occurrencesOutOfReach` restent ici.
 */
const CanvasFormatEverywhereReceiptSchema = z
  .object({
    /** Occurrences du texte dans les paragraphes du corps, cellules de tableau comprises, ou du lieu demandé : celles que l'écriture vise. Zéro n'existe pas : l'appel est refusé. */
    occurrencesFound: z.number().int().positive(),
    /** Occurrences écrites par cet appel. Absent sous un filtre `onlyWhere`. */
    occurrencesWritten: z.number().int().nonnegative().optional(),
    /** Occurrences où l'écriture n'aurait rien changé : non réécrites, jugées par la relecture avec les autres. Absent : aucune. */
    occurrencesAlreadyFormatted: z.number().int().positive().optional(),
    /** Occurrences que l'éditeur n'a PAS écrites : le plan qui les portait a été refusé après d'autres plans réussis. Absent : aucune. */
    occurrencesNotWritten: z.number().int().positive().optional(),
    /** Occurrences laissées telles quelles plutôt qu'écrites au mauvais endroit, groupées par raison, leurs paragraphes nommés. Absent : aucune. */
    occurrencesSetAside: z.array(groupeNonEcrit(CanvasFormatSetAsideReasonSchema)).min(1).optional(),
    /**
     * Occurrences que portent des paragraphes hors de portée des verbes de
     * forme (paragraphes sans identifiant ou à l'identifiant en double ; cellules
     * de tableau d'un en-tête, d'un pied ou d'une note) : ni écrites,
     * ni comptées dans `occurrencesFound`. Leurs paragraphes sont dans
     * `skipped`. Absent : aucune.
     */
    occurrencesOutOfReach: z.number().int().positive().optional(),
    /** Présent quand la cible donnait une LISTE de textes : le compte de chacun. */
    texts: CanvasFormatTextsReceiptSchema.optional(),
  })
  .strict()
  .superRefine((partout, ctx) => {
    const ecartees = (partout.occurrencesSetAside ?? []).reduce((somme, groupe) => somme + groupe.count, 0);
    // WHY : « written » par texte suit `occurrencesWritten` — présent sans filtre, absent sous un filtre, pour chaque texte.
    const sansEcrites = (partout.texts?.perText ?? []).filter((texte) => texte.written === undefined).length;
    if (sansEcrites !== (partout.occurrencesWritten === undefined ? partout.texts?.perText?.length ?? 0 : 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['texts', 'perText'],
        message: 'format.everywhere.texts.perText: "written" must be present on every text exactly when "occurrencesWritten" is '
          + `(got ${sansEcrites} text(s) without it, occurrencesWritten ${partout.occurrencesWritten === undefined ? 'absent' : 'present'}).`,
      });
    }
    if (partout.occurrencesWritten === undefined) {
      // WHY : sous un filtre, les sorts sont ceux des passages ; deux comptes de sorts laisseraient le modèle choisir lequel croire.
      if (partout.occurrencesAlreadyFormatted !== undefined || partout.occurrencesNotWritten !== undefined || partout.occurrencesSetAside !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'format.everywhere: without "occurrencesWritten" (an onlyWhere filter decides what is written), the other occurrence outcomes must be absent too.',
        });
      }
      return;
    }
    for (const groupe of partout.occurrencesSetAside ?? []) {
      if (groupe.nodeIds === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['occurrencesSetAside'],
          message: `format.everywhere sets ${groupe.count} occurrence(s) aside for reason "${groupe.reason}" without naming their paragraphs: "nodeIds" is required here.`,
        });
      }
    }
    const sorts = partout.occurrencesWritten + (partout.occurrencesAlreadyFormatted ?? 0) + (partout.occurrencesNotWritten ?? 0) + ecartees;
    if (sorts !== partout.occurrencesFound) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `format.everywhere: occurrencesFound (${partout.occurrencesFound}) must equal occurrencesWritten + occurrencesAlreadyFormatted `
          + `+ occurrencesNotWritten + the occurrencesSetAside counts (${sorts}).`,
      });
    }
  });

/**
 * Ce qu'une remise à zéro retire au-delà des réglages que les outils de forme
 * posent (alignement, retraits, espacement, interligne ; les sept réglages de
 * texte), par catégorie. `border` : la bordure du paragraphe, barre de
 * changement comprise. `paragraphMark` : la forme de la marque de paragraphe.
 * `otherParagraphFormatting`, `otherTextFormatting` : tout autre élément posé à
 * la main (barré, capitales, exposant… pour le texte).
 */
export const CanvasFormatResetRemovedSchema = z.enum([
  'border',
  'tabs',
  'shading',
  'keepWithNext',
  'keepLines',
  'widowControl',
  'contextualSpacing',
  'frame',
  'paragraphMark',
  'otherParagraphFormatting',
  'otherTextFormatting',
]);
export type CanvasFormatResetRemoved = z.infer<typeof CanvasFormatResetRemovedSchema>;

/**
 * Ce qu'une remise à zéro (`reset`, #1867) a fait au-delà des réglages des
 * outils. Le saut de page avant et le niveau de plan n'y figurent pas : ils
 * sont de la structure, la remise à zéro les garde.
 */
const CanvasFormatResetReceiptSchema = z
  .object({
    /**
     * Ce qui a été retiré, par catégorie, avec le nombre de paragraphes qui le
     * portaient avant l'écriture et ne le portent plus au fichier RELU. Absent :
     * rien de tel, ou le fichier n'a pas pu être relu.
     */
    removed: z
      .array(z.object({ what: CanvasFormatResetRemovedSchema, paragraphs: z.number().int().positive() }).strict())
      .min(1)
      .optional(),
    /**
     * Ce qui est encore au fichier après la remise à zéro, par élément
     * (`pPr/…`, `rPr/…`), avec le nombre de paragraphes qui le portent : ce que
     * l'éditeur ne sait pas retirer du texte (ces paragraphes ne sont pas dits
     * « différents » pour autant : redemander n'y changerait rien), et ce dont
     * la relecture ne confirme pas le retrait. Absent : rien de tel.
     */
    notRemoved: z
      .array(z.object({ element: z.string().min(1), paragraphs: z.number().int().positive() }).strict())
      .min(1)
      .optional(),
    /**
     * Les paragraphes dont la forme de PARAGRAPHE a été remise à zéro et la
     * forme de TEXTE laissée telle quelle : leur texte porte une révision en
     * attente (forme de texte, déplacement) sur laquelle l'éditeur refuse
     * d'écrire. Le réglage `reset` ne juge que leur forme de paragraphe.
     * Absent : aucun.
     */
    textFormattingLeft: z
      .object({ reason: z.literal('pending_tracked_change'), ...paragraphesNommes })
      .strict()
      .optional(),
  })
  .strict();

/**
 * Pourquoi une copie de forme (`copy_format`, #1869) n'a pas copié un réglage
 * du paragraphe modèle, sur aucune cible. `unreadable_on_source` : sa valeur ne
 * se lit pas sur le modèle (style absent du fichier, couleur de thème,
 * interligne fixé en points, exposant, trait de bordure incomplet…) — elle
 * n'est jamais devinée. `source_has_no_text` : le modèle n'a pas de texte,
 * donc pas de forme de texte à copier. `source_has_no_named_style` : le modèle
 * ne porte aucun style nommé, ou un style que le fichier ne définit pas.
 * `style_numbers_paragraphs` : le style du modèle porte une numérotation ; le
 * poser numéroterait les cibles, ce qu'une copie de forme ne fait pas.
 * `border_line_type_not_measured` : un côté de la bordure du modèle est d'un
 * type de trait autre que `single`, `double`, `dotted`, `dashed` — les seuls
 * dont l'écriture par l'éditeur est mesurée.
 */
export const CanvasFormatNotCopiedReasonSchema = z.enum(['unreadable_on_source', 'source_has_no_text', 'source_has_no_named_style', 'style_numbers_paragraphs', 'border_line_type_not_measured']);
export type CanvasFormatNotCopiedReason = z.infer<typeof CanvasFormatNotCopiedReasonSchema>;

/** Nombre maximal de passages décrits par cible dans `format.copy.textFormattingLeft.shown`. Valeur de jugement. */
export const CANVAS_FORMAT_COPY_SHOWN_RUNS_MAX = 5;

/** Les paragraphes d'un groupe laissé par une copie : nommés jusqu'à la borne, comptés au-delà. */
const paragraphesLaissesParLaCopie = <Raison extends string>(raison: Raison) => z
  .object({
    reason: z.literal(raison),
    ...paragraphesNommes,
  })
  .strict();

/**
 * Ce qu'une copie de forme (`copy_format`, #1869) a copié, et ce qu'elle a
 * laissé. Les réglages copiés sont jugés un par un dans `settings`, où
 * `requested` est la valeur lue sur le paragraphe modèle.
 */
const CanvasFormatCopyReceiptSchema = z
  .object({
    /** Le paragraphe modèle. */
    from: z.string().min(1),
    /**
     * Le passage du modèle dont la forme de texte a été copiée : le plus long
     * (le premier, à longueur égale). `passagesInSource` > 1 : le modèle porte
     * plusieurs formes de texte, une seule est copiée. Absent : aucune forme de
     * texte n'a été copiée.
     */
    textFormFrom: z
      .object({
        text: z.string().min(1).max(CANVAS_FORMAT_DIFFERENCE_TEXT_MAX),
        passagesInSource: z.number().int().positive(),
      })
      .strict()
      .optional(),
    /** Les réglages du modèle qui n'ont été copiés sur aucune cible, avec la raison. `border` vaut pour les quatre côtés. Absent : tous l'ont été. */
    notCopied: z
      .array(z.object({ setting: z.enum(CANVAS_FORMAT_SETTING_KEYS), reason: CanvasFormatNotCopiedReasonSchema }).strict())
      .min(1)
      .optional(),
    /**
     * Les côtés `between` et `bar` que le modèle ou une cible porte à la main
     * dans sa bordure : une copie ne les lit ni ne les écrit, ils restent tels
     * quels de part et d'autre. Absent : aucun.
     */
    borderSidesNotCopied: z.array(z.enum(['between', 'bar'])).min(1).optional(),
    /**
     * Les cibles dont les RETRAITS n'ont pas été copiés : le modèle ou la cible
     * est un paragraphe numéroté, dont les retraits tiennent à la liste. Absent : aucune.
     */
    indentsKept: paragraphesLaissesParLaCopie('list_numbering').optional(),
    /**
     * Les cibles dont le STYLE NOMMÉ n'a pas été changé : leur style porte leur
     * numérotation, le remplacer la leur ferait perdre. Absent : aucune.
     */
    styleKept: paragraphesLaissesParLaCopie('list_numbering').optional(),
    /**
     * Les cibles dont la forme de TEXTE n'a pas été écrite : leur texte porte
     * une révision en attente (forme de texte, déplacement) sur laquelle
     * l'éditeur refuse d'écrire. Leur style et leur forme de paragraphe sont
     * copiés. `shown` dit, pour les premières, ce que leur texte montre au
     * fichier relu — chaque passage, ses réglages rangés par origine : ce que
     * donne le style posé, ce qui reste posé à la main. Absent : aucune.
     */
    textFormattingLeft: paragraphesLaissesParLaCopie('pending_tracked_change')
      .extend({
        shown: z
          .array(z.object({ nodeId: z.string().min(1), runs: z.array(CanvasRunFormatReadSchema).max(CANVAS_FORMAT_COPY_SHOWN_RUNS_MAX) }).strict())
          .min(1)
          .max(CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX)
          .optional(),
      })
      .strict()
      .optional(),
    /**
     * Les éléments de forme de caractère que le MODÈLE porte à la main hors des
     * sept réglages de texte (noms du fichier : `strike`, `caps`, `vertAlign`…) :
     * ils ne sont copiés sur aucune cible, et retirés de celles qui en portent.
     * Absent : aucun.
     */
    textFormattingNotCopied: z.array(z.string().min(1)).min(1).optional(),
    /**
     * Les éléments de forme de caractère qu'une cible porte à la main et que
     * l'éditeur ne sait pas retirer, avec le nombre de cibles qui les portent.
     * Absent : aucun.
     */
    textFormattingNotRemoved: z
      .array(z.object({ element: z.string().min(1), paragraphs: z.number().int().positive() }).strict())
      .min(1)
      .optional(),
    /**
     * La forme de paragraphe qu'une copie ne reprend pas, que des cibles
     * portent à la main et que le modèle ne porte pas : elle RESTE sur ces
     * cibles, qui diffèrent donc du modèle sur ce point. Absent : aucune.
     */
    paragraphFormattingKept: z
      .array(z.object({ what: z.enum(['shading', 'keepWithNext', 'keepLines', 'widowControl']), paragraphs: z.number().int().positive() }).strict())
      .min(1)
      .optional(),
    /**
     * Nombre de paragraphes — cibles, et le modèle s'il les touche — qui se
     * suivent avec la même bordure après la copie : la page, comme Word, les
     * peint comme UN seul cadre, sans trait entre eux. Absent : aucun.
     */
    borderedNeighbours: z.number().int().min(2).optional(),
  })
  .strict();

/**
 * Ce qu'une écriture de forme a fait, établi par une relecture du fichier Word
 * exporté après l'écriture — jamais par l'accusé de l'éditeur.
 */
export const CanvasFormatWriteReceiptSchema = z
  .object({
    /** Le lieu où l'appel a écrit, tel qu'il a été demandé. Absent : le corps de l'acte. */
    in: CanvasFormatPlaceSchema.optional(),
    /** Paragraphes écrits par cet appel, entièrement (1 pour un passage). */
    paragraphsWritten: z.number().int().nonnegative(),
    /**
     * Parmi les paragraphes où `format_text` a écrit, ceux qui sont des
     * paragraphes de cellule de tableau du corps : une forme de TEXTE sur « tout
     * l'acte » ou une plage vaut aussi dans les tableaux. Absent : aucun.
     */
    tableCellParagraphsWritten: z.number().int().positive().optional(),
    /**
     * Écritures appliquées, chacune une entrée de l'historique de l'éditeur :
     * autant d'annulations (Ctrl+Z, `undo_changes`) défont l'appel. Un plan
     * atomique en vaut une pour tous ses paragraphes ; retraits, espacement et
     * interligne en valent une par paragraphe.
     */
    writes: z.number().int().nonnegative(),
    /**
     * Écritures de cet appel qu'aucune annulation ne défait plus : les
     * premières, sorties de l'historique de l'éditeur, qui ne garde que ses
     * {@link CANVAS_UNDO_HISTORY_DEPTH} dernières entrées. Les
     * `writes - writesNotUndoable` dernières se défont. Absent : tout l'appel
     * se défait.
     */
    writesNotUndoable: z.number().int().positive().optional(),
    /**
     * Paragraphes de la cible où l'écriture n'aurait rien changé — ils
     * portaient déjà, posés à la main, tous les réglages demandés, ou n'avaient
     * aucune forme directe à retirer : ils n'ont pas été réécrits, et la
     * relecture les juge avec les autres. Absent : aucun.
     */
    paragraphsAlreadyFormatted: z.number().int().positive().optional(),
    /**
     * Paragraphes visés que l'éditeur n'a PAS écrits, ou pas entièrement :
     * l'écriture qui les portait a été refusée après d'autres écritures
     * réussies. Absent : tout ce qui devait être écrit l'a été.
     */
    paragraphsNotWritten: z.number().int().positive().optional(),
    /**
     * Parmi {@link paragraphsNotWritten}, ceux qui ont reçu UNE PARTIE de leurs
     * écritures avant l'interruption (l'alignement sans les retraits, les
     * retraits sans l'espacement). Absent : aucun.
     */
    paragraphsPartlyWritten: z.number().int().positive().optional(),
    /**
     * Un reçu par réglage demandé. Vide seulement quand un filtre `onlyWhere`
     * ou un texte visé dans tout l'acte n'a laissé aucun passage à juger (rien
     * trouvé, ou tout écarté) : aucun réglage n'est alors dit conforme, faute
     * de texte relu.
     */
    settings: z.array(CanvasFormatSettingReceiptSchema),
    /** Présent quand l'appel portait un filtre `onlyWhere` : les passages trouvés et leur sort. */
    onlyWhere: CanvasFormatFilterReceiptSchema.optional(),
    /** Présent quand la cible était un texte exact sans paragraphe : ses occurrences dans l'acte et leur sort. */
    everywhere: CanvasFormatEverywhereReceiptSchema.optional(),
    /** Présent après une remise à zéro (`reset`) qui a retiré, ou laissé, autre chose que les réglages des outils de forme. */
    reset: CanvasFormatResetReceiptSchema.optional(),
    /** Présent pour une copie de forme (`copy_format`) : le modèle, ce qui en a été copié et ce qui a été laissé. */
    copy: CanvasFormatCopyReceiptSchema.optional(),
    /**
     * Paragraphes couverts par la cible et non écrits, comptés par raison.
     * Ceux qui ne le sont pas à cause de ce qu'ils portent ({@link CanvasFormatEditorRefusalReasonSchema})
     * sont NOMMÉS (`nodeIds`, au plus {@link CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX},
     * `paragraphsNotListed` au-delà) : l'agent les redit à l'avocat. Absent :
     * tout ce que la cible couvre a été écrit.
     */
    skipped: z.array(groupeNonEcrit(CanvasFormatWriteSkippedReasonSchema)).min(1).optional(),
    /**
     * Présent quand l'éditeur a refusé un lot APRÈS en avoir joué une étape —
     * dans cet appel ou dans un appel précédent sur le même document monté —
     * et qu'aucune écriture n'a relancé son peintre depuis : sa page ne se
     * repeint plus (défaut de l'éditeur,
     * https://github.com/hector-ai-legal/hector-monorepo/issues/1913). Ce que
     * l'appel a écrit est au fichier — c'est lui que le reçu a relu — mais la
     * page peut ne pas le montrer, ni ce que l'avocat tape ensuite. Absent : la
     * page suit.
     */
    pageNotRepainted: z.literal(true).optional(),
    /**
     * Les paragraphes ÉCRITS alors qu'ils portent un changement de forme de
     * paragraphe en attente (`w:pPrChange`), pour tout verbe qui écrit une forme
     * de paragraphe (`format_paragraphs`, `copy_format`) : la révision reste à
     * accepter ou à rejeter, mais la forme écrite fait désormais partie de ce
     * qu'elle propose — rejetée, elle l'emporte avec elle. Absent : aucun.
     */
    writtenOverPendingParagraphRevision: z.object(paragraphesNommes).strict().optional(),
    /** Identifiants demandés qui ne désignent aucun paragraphe que le verbe écrit — corps et cellules de tableau du corps —, avec la raison. */
    unresolved: z
      .array(z.object({ nodeId: z.string().min(1), reason: CanvasFormatUnresolvedReasonSchema }).strict())
      .min(1)
      .optional(),
  })
  .strict()
  // WHY : `conforming` répond pour la demande ENTIÈRE. Tant que des paragraphes
  // visés ne sont pas écrits, aucun réglage ne peut le dire.
  .superRefine((recu, ctx) => {
    if ((recu.paragraphsPartlyWritten ?? 0) > (recu.paragraphsNotWritten ?? 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paragraphsPartlyWritten'],
        message: `format receipt reports ${recu.paragraphsPartlyWritten} partly written paragraph(s) among ${recu.paragraphsNotWritten ?? 0} not written: `
          + 'partly written paragraphs are counted within paragraphsNotWritten.',
      });
    }
    // WHY : sous un filtre, les sorts sont ceux des passages (`onlyWhere`) ; sans filtre, ceux des occurrences. Jamais les deux, jamais aucun.
    if (recu.everywhere !== undefined && (recu.everywhere.occurrencesWritten === undefined) !== (recu.onlyWhere !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['everywhere'],
        message: 'format receipt: "everywhere.occurrencesWritten" must be present exactly when there is no onlyWhere filter; '
          + `got ${recu.everywhere.occurrencesWritten === undefined ? 'none' : 'one'} with ${recu.onlyWhere === undefined ? 'no' : 'an'} onlyWhere filter.`,
      });
    }
    // WHY : sans passage jugé, « conforme » ne reposerait sur aucune relecture ; avec, un reçu sans réglage ne dirait rien de la demande.
    const compter = (groupes: ReadonlyArray<{ count: number }> | undefined): number => (groupes ?? []).reduce((somme, groupe) => somme + groupe.count, 0);
    const aucunPassageJuge = recu.onlyWhere !== undefined
      ? recu.onlyWhere.passagesFound === compter(recu.onlyWhere.passagesSetAside)
      : recu.everywhere !== undefined && recu.everywhere.occurrencesFound === compter(recu.everywhere.occurrencesSetAside);
    if ((recu.settings.length === 0) !== aucunPassageJuge) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['settings'],
        message: 'format receipt: "settings" must be empty exactly when an onlyWhere filter, or a text targeted everywhere, left no passage to judge '
          + `(none found, or all set aside); got ${recu.settings.length} setting(s) with `
          + `${recu.onlyWhere !== undefined ? `${recu.onlyWhere.passagesFound} passage(s) found` : recu.everywhere !== undefined ? `${recu.everywhere.occurrencesFound} occurrence(s) found` : 'neither'}.`,
      });
    }
    // WHY : un paragraphe que l'éditeur refuse d'écrire est un paragraphe visé non écrit, et il se nomme.
    const refuses = (recu.skipped ?? []).filter((groupe) => estUnRefusDeLEditeur(groupe.reason));
    for (const groupe of refuses) {
      if (groupe.nodeIds === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['skipped'],
          message: `format receipt skips ${groupe.count} paragraph(s) for reason "${groupe.reason}" without naming them: `
            + 'a paragraph the editor refuses to write must be listed in "nodeIds".',
        });
      }
    }
    const nonEcrits = (recu.paragraphsNotWritten ?? 0) + refuses.reduce((somme, groupe) => somme + groupe.count, 0);
    if (nonEcrits === 0) return;
    const conformes = recu.settings.filter((reglage) => reglage.outcome === 'conforming').map((reglage) => reglage.setting);
    if (conformes.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['settings'],
        message: `format receipt reports ${nonEcrits} targeted paragraph(s) not written (paragraphsNotWritten, or skipped because the editor refuses them), `
          + `so no setting may be "conforming" (got conforming for: ${conformes.join(', ')}); `
          + 'the unwritten paragraphs must be counted as unreadable with reason "not_written".',
      });
    }
  });
export type CanvasFormatWriteReceipt = z.infer<typeof CanvasFormatWriteReceiptSchema>;
